// Core Case Management Types - Mirror of Backend Entities

// ================================
// Type Constants (instead of enums for erasableSyntaxOnly compatibility)
// ================================

/**
 * Case State Enum - Matches backend CaseStatus.java exactly
 * Backend sends integer values, frontend displays string labels
 */
export const CaseState = {
  Open: 0,
  InProgress: 1,
  AwaitingUpload: 2,
  PendingReview: 3,
  Escalated: 4,
  Rejected: 5,
  Closed: 6,
  Waived: 7,
  Resolved: 8,
  // Compliance-specific states (100+)
  ComplianceReview: 100,
  ComplianceApproved: 101,
  ComplianceRejected: 102,
  ComplianceEscalated: 103,
  ComplianceClosed: 104,
  ComplianceConfirmed: 105,
} as const;

export type CaseState = (typeof CaseState)[keyof typeof CaseState];

/**
 * Map case state integer to display label
 */
export const CaseStateLabels: Record<number, string> = {
  [CaseState.Open]: "Open",
  [CaseState.InProgress]: "In Progress",
  [CaseState.AwaitingUpload]: "Awaiting Upload",
  [CaseState.PendingReview]: "Pending Review",
  [CaseState.Escalated]: "Escalated",
  [CaseState.Rejected]: "Rejected",
  [CaseState.Closed]: "Closed",
  [CaseState.Waived]: "Waived",
  [CaseState.Resolved]: "Resolved",
  [CaseState.ComplianceReview]: "Compliance Review",
  [CaseState.ComplianceApproved]: "Compliance Approved",
  [CaseState.ComplianceRejected]: "Compliance Rejected",
  [CaseState.ComplianceEscalated]: "Compliance Escalated",
  [CaseState.ComplianceClosed]: "Compliance Closed",
  [CaseState.ComplianceConfirmed]: "Compliance Confirmed",
};

/**
 * Get display label for case state
 */
export function getCaseStateLabel(state: number | CaseState): string {
  return CaseStateLabels[state] || "Unknown";
}

/**
 * Active case management workflow states — the single source of truth for
 * status filter dropdowns across Dashboard, CasesPage, and any future consumer.
 * Excludes internal/legacy states like AwaitingUpload, PendingReview,
 * Escalated, Rejected, and Waived that are not part of the current workflow.
 */
export const CASE_MANAGEMENT_STATES: CaseState[] = [
  CaseState.Open,
  CaseState.InProgress,
  CaseState.ComplianceReview,
  CaseState.ComplianceApproved,
  CaseState.ComplianceRejected,
  CaseState.ComplianceEscalated,
  CaseState.ComplianceClosed,
  CaseState.ComplianceConfirmed,
];

/**
 * Open/active case states — all workflow states except terminal compliance states.
 * Shared between Dashboard KPI and Open Cases page so counts always match.
 */
export const OPEN_CASE_STATES: CaseState[] = CASE_MANAGEMENT_STATES.filter(
  (s) =>
    s !== CaseState.ComplianceApproved && s !== CaseState.ComplianceConfirmed,
);

/**
 * Check if state is a compliance-specific state
 */
export function isComplianceState(state: number | CaseState): boolean {
  return typeof state === "number" && state >= 100;
}

/**
 * Check if state is a regular case state
 */
export function isRegularState(state: number | CaseState): boolean {
  return typeof state === "number" && state < 100;
}

export const CasePriority = {
  LOW: "LOW",
  MEDIUM: "MEDIUM",
  HIGH: "HIGH",
  CRITICAL: "CRITICAL",
} as const;

export type CasePriority = (typeof CasePriority)[keyof typeof CasePriority];

export const CaseSeverity = {
  LOW: "LOW",
  MEDIUM: "MEDIUM",
  HIGH: "HIGH",
  CRITICAL: "CRITICAL",
} as const;

export type CaseSeverity = (typeof CaseSeverity)[keyof typeof CaseSeverity];

export const AssignmentSourceType = {
  Rule: "Rule",
  Chapter: "Chapter",
  Document: "Document",
  CompanyDefault: "CompanyDefault",
} as const;

export type AssignmentSourceType =
  (typeof AssignmentSourceType)[keyof typeof AssignmentSourceType];

export const ComplianceNodeType = {
  Document: "Document",
  Chapter: "Chapter",
  Rule: "Rule",
  SubRule: "SubRule",
} as const;

export type ComplianceNodeType =
  (typeof ComplianceNodeType)[keyof typeof ComplianceNodeType];

export const ActionType = {
  CaseCreated: "CaseCreated",
  StateChanged: "StateChanged",
  UserAssigned: "UserAssigned",
  UserUnassigned: "UserUnassigned",
  CommentAdded: "CommentAdded",
  AttachmentAdded: "AttachmentAdded",
  AttachmentRemoved: "AttachmentRemoved",
  CaseEscalated: "CaseEscalated",
  CaseRejected: "CaseRejected",
} as const;

export type ActionType = (typeof ActionType)[keyof typeof ActionType];

// ================================
// API Response Wrappers
// ================================

/**
 * Standard API response wrapper from backend
 */
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: {
    code: string;
    message: string;
  };
  timestamp: number;
}

/**
 * Paginated response structure
 */
export interface PaginatedResponse<T> {
  data: T[];
  page: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
}

// ================================
// Core Entities
// ================================

export interface CaseUserAssignment {
  id: string;
  caseId: string;
  assignedUserId?: string;
  assignmentSource: AssignmentSourceType;
  assignedByUserId: string;
  assignedOn: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CaseComment {
  id: number;
  caseId: number;
  commentText: string;
  content: string; // Alias for commentText
  createdBy: {
    id: number;
    name: string;
    username: string;
  };
  createdAt: string;
  updatedBy?: {
    id: number;
    name: string;
    username: string;
  } | null;
  updatedAt?: string | null;
  isEdited: boolean;
  editCount: number;
}

export interface CaseAttachment {
  id: number;
  caseId: number;
  name: string;
  fileName: string;
  originalFilename: string;
  fileSize: number;
  contentType: string;
  filePath: string;
  s3Key: string;
  contentHash: string;
  type: number; // 0 = INDIVIDUAL, 1 = DOCUMENT_GROUP
  status: number; // 0 = READY, 1 = PROCESSING, 2 = ERROR, 3 = PDF_GENERATED
  isDocumentGroup: boolean;
  tags: string[];
  uploadedBy: number;
  uploadedAt: string;
  description?: string;
  groupedAttachments?: CaseAttachment[]; // Only for document groups
}

export interface CaseActionLog {
  id: number;
  caseId: number;
  actionType: ActionType;
  description: string;
  performedByUserId?: string;
  performedByUser?: UserInfo;
  performedAt: string;
  metadata?: Record<string, any>;
  isSystemGenerated: boolean;
}

export interface Case {
  id: number;
  caseNumber?: string;
  title: string;
  description?: string;
  state: number; // Case status value (0-8, 100-104)
  caseStatus?: string; // Case status enum name (e.g., "IN_PROGRESS")
  priority?: string;
  severity?: CaseSeverity;
  dueDate?: string;
  createdAt: string;
  updatedAt: string;
  companyId: string;
  ruleId?: number;

  // User tracking
  createdBy: {
    id: number;
    name: string;
    username: string;
  };
  updatedBy: {
    id: number;
    name: string;
    username: string;
  };
  assignedTo?: {
    id: number;
    tenantId: number;
    firstName: string;
    lastName: string;
    username: string;
    name: string;
    email: string;
    emailVerified: boolean;
    mfaEnabled: boolean;
    status: number;
    createdBy?: {
      id: number;
      name: string;
      username: string;
    };
    createdAt?: string;
    updatedBy?: {
      id: number;
      name: string;
      username: string;
    };
    updatedAt?: string;
    lastLoginAt?: string;
    roles?: Array<{
      id: number;
      name: string;
    }>;
  } | null;

  // Manual case creation fields
  creationType?: string; // "MANUAL", "AUTO", or "COMPLIANCE_CONFIRMED"
  resolutionInstructions?: string | null;
  nonComplianceReason?: string | null;

  // Count fields from API
  commentCount?: number | null;
  attachmentCount?: number | null;
  linkedRuleDocumentCount?: number | null;
  caseDocumentCount?: number | null;

  // Legacy/computed properties - kept for backward compatibility
  // These are NOT in the API response, populated by frontend
  complianceNodeId?: string;
  complianceNode?: ComplianceNode;
  createdByUserId?: string;
  createdByUser?: UserInfo;
  sourceRuleId?: string;
  sourceChapterId?: string;
  rule?: any;
  chapter?: string;
  documents?: any[];
  documentIds?: number[];
  roleId?: number;
  tags?: string[];
  regulatoryBody?: string;
  complianceFramework?: string;
  potentialFine?: string;
  complianceNodeTitle?: string;
  complianceNodeDescription?: string;

  // Navigation properties - NOT in API response, loaded separately
  userAssignments?: CaseUserAssignment[];
  comments?: CaseComment[];
  attachments?: CaseAttachment[];
  actionLogs?: CaseActionLog[];
  assignedUsers?: UserInfo[];
  currentAssignees?: UserInfo[];
  complianceNodePath?: ComplianceNodePath[];
}

export interface ComplianceNode {
  id: string;
  title: string;
  description?: string;
  nodeType: ComplianceNodeType;
  parentId?: string;
  companyId: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;

  // Navigation properties
  parent?: ComplianceNode;
  children?: ComplianceNode[];
  nodeResponsibilityAssignment?: NodeResponsibilityAssignment;

  // Computed properties for UI
  fullPath?: string;
  level?: number;
  hasChildren?: boolean;
}

export interface NodeResponsibilityAssignment {
  id: string;
  complianceNodeId: string;
  complianceNode?: ComplianceNode;
  primaryUserId?: string;
  primaryUser?: UserInfo;
  secondaryUserIds: string[];
  secondaryUsers?: UserInfo[];
  companyId: string;
  createdAt: string;
  updatedAt: string;
  isInherited: boolean;
  inheritedFromNodeId?: string;
  inheritedFromNode?: ComplianceNode;
}

// ================================
// Supporting Types
// ================================

export interface UserInfo {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;
  avatar?: string;
  role?: string;
  isActive: boolean;
}

export interface ComplianceNodePath {
  id: string;
  title: string;
  nodeType: ComplianceNodeType;
  level: number;
}

export interface Company {
  id: string;
  name: string;
  isActive: boolean;
}

// ================================
// Request/Response Types
// ================================

/**
 * Create Case Request
 * POST /api/case-management/cases
 *
 * API Contract Fields:
 * - ruleId: number - The compliance rule ID
 * - title: string - Case title
 * - description: string - Case description
 * - assignedTo: number - User ID to assign case to
 * - dueDate: string - ISO date string
 * - priority: string - "LOW" | "MEDIUM" | "HIGH" | "CRITICAL"
 * - severity: string - "LOW" | "MEDIUM" | "HIGH" | "CRITICAL"
 * - sourceChapterId: string - Source chapter ID
 * - secondaryUserId: string - Secondary user ID
 * - assignmentLevel: number - Assignment level
 * - createdByUserId: string - Created by user ID
 * - roleId: number - Role ID
 */
export interface CreateCaseRequest {
  // API Contract fields (correct field names)
  ruleId?: number;
  title: string;
  description?: string;
  assignedTo?: number;
  dueDate?: string; // ISO date string: "2026-01-12T03:13:14.180Z"
  priority?: string;
  severity?: "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";
  sourceChapterId?: string;
  secondaryUserId?: string;
  assignmentLevel?: number;
  createdByUserId?: string;
  roleId?: number;
  resolutionInstructions?: string;
  nonComplianceReason?: string;

  // Legacy fields (deprecated - for backward compatibility only)
  /** @deprecated Use ruleId instead */
  companyId?: string;
  /** @deprecated Use sourceChapterId instead */
  complianceNodeId?: string;
  /** @deprecated Use ruleId instead */
  sourceRuleId?: string;
  /** @deprecated Use assignedTo instead */
  primaryUserId?: string;
}

/**
 * Update Case Request
 * PUT /api/case-management/cases/{id}
 *
 * API Contract Fields:
 * - ruleId: number - The compliance rule ID (required)
 * - title: string - Case title (required)
 * - description: string - Case description
 * - assignedTo: number - User ID to assign case to
 * - dueDate: string - ISO date string
 * - priority: string - "LOW" | "MEDIUM" | "HIGH" | "CRITICAL"
 * - severity: string - "LOW" | "MEDIUM" | "HIGH" | "CRITICAL"
 * - sourceChapterId: string - Source chapter ID
 * - secondaryUserId: string - Secondary user ID
 * - assignmentLevel: number - Assignment level
 * - createdByUserId: string - Created by user ID
 * - roleId: number - Role ID
 */
export interface UpdateCaseRequest {
  ruleId: number;
  title: string;
  description?: string;
  assignedTo?: number;
  dueDate?: string; // ISO date string: "2026-01-23T15:08:57.172Z"
  priority?: string; // "HIGH" | "MEDIUM" | "LOW" or string
  severity?: "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";
  sourceChapterId?: string;
  secondaryUserId?: string;
  assignmentLevel?: number;
  createdByUserId?: string;
  roleId?: number;
  nonComplianceReason?: string;
}

export interface UpdateCaseStateRequest {
  newState: CaseState;
  reason?: string;
}

export interface AssignUserRequest {
  userId: string;
  assignmentSource?: AssignmentSourceType;
}

export interface CreateCommentRequest {
  commentText: string;
  isInternal?: boolean;
}

export interface UpdateCommentRequest {
  commentText?: string;
  isInternal?: boolean;
}

export interface UpdateStatusRequest {
  status: number; // 0=PENDING, 1=ACTIVE, 2=SUSPENDED, 3=COMPLETED, 4=DELETED
}

export interface CreateNodeResponsibilityRequest {
  complianceNodeId: string;
  primaryUserId?: string;
  secondaryUserIds: string[];
}

export interface BulkAssignmentRequest {
  nodeIds: string[];
  primaryUserId?: string;
  secondaryUserIds: string[];
  overrideExisting: boolean;
}

// ================================
// Filter and Query Types
// ================================

export interface CaseFilters {
  states?: CaseState[];
  priorities?: CasePriority[];
  complianceNodeIds?: string[];
  assignedUserIds?: string[];
  dueDateFrom?: string;
  dueDateTo?: string;
  createdDateFrom?: string;
  createdDateTo?: string;
  searchText?: string;
  creationType?: "MANUAL" | "AUTO" | "COMPLIANCE_CONFIRMED"; // ALH-194: Filter by creation type
  pageSize?: number;
  pageNumber?: number;
  sortBy?: string;
  sortDirection?: "asc" | "desc";
}

export interface CaseInboxResponse {
  cases: Case[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface DashboardMetrics {
  totalCases: number;
  openCases: number;
  inProgressCases: number;
  overdueToday: number;
  dueThisWeek: number;
  recentlyCreated: number;
  recentlyClosed: number;
  averageResolutionTime: number;

  // By priority
  criticalCases: number;
  highPriorityCases: number;

  // By compliance node
  casesByChapter: Array<{
    chapterId: string;
    chapterTitle: string;
    caseCount: number;
  }>;

  // Trends
  casesCreatedTrend: Array<{
    date: string;
    count: number;
  }>;

  casesClosedTrend: Array<{
    date: string;
    count: number;
  }>;
}

export interface ResponsibilityMatrixRow {
  complianceNode: ComplianceNode;
  primaryUser?: UserInfo;
  secondaryUsers: UserInfo[];
  isInherited: boolean;
  inheritedFromNode?: ComplianceNode;
  caseCount: number;
  openCaseCount: number;
  overdueCaseCount: number;
}

export interface EligibleUsersResponse {
  users: UserInfo[];
  recommendations: Array<{
    user: UserInfo;
    reason: string;
    confidence: number;
  }>;
}

// ================================
// Real-time Event Types
// ================================

export interface CaseCreatedEvent {
  caseId: string;
  case: Case;
  companyId: string;
  timestamp: string;
}

export interface CaseStateChangedEvent {
  caseId: string;
  oldState: CaseState;
  newState: CaseState;
  changedByUserId: string;
  changedByUser?: UserInfo;
  reason?: string;
  timestamp: string;
}

export interface CaseUserAssignedEvent {
  caseId: string;
  assignedUserId: string;
  assignedUser?: UserInfo;
  assignedByUserId: string;
  assignedByUser?: UserInfo;
  assignmentSource: AssignmentSourceType;
  timestamp: string;
}

export interface CaseUserUnassignedEvent {
  caseId: string;
  unassignedUserId: string;
  unassignedUser?: UserInfo;
  unassignedByUserId: string;
  unassignedByUser?: UserInfo;
  timestamp: string;
}

export interface CaseCommentAddedEvent {
  caseId: string;
  comment: CaseComment;
  timestamp: string;
}

export interface CaseAttachmentAddedEvent {
  caseId: string;
  attachment: CaseAttachment;
  timestamp: string;
}

export type CaseEvent =
  | CaseCreatedEvent
  | CaseStateChangedEvent
  | CaseUserAssignedEvent
  | CaseUserUnassignedEvent
  | CaseCommentAddedEvent
  | CaseAttachmentAddedEvent;

// ================================
// UI State Types
// ================================

export interface CaseListState {
  filters: CaseFilters;
  selectedCaseIds: string[];
  sortBy: string;
  sortDirection: "asc" | "desc";
  viewMode: "list" | "grid" | "compact";
}

export interface CaseDetailState {
  activeTab: "overview" | "activity" | "comments" | "attachments" | "history";
  showAssignmentDialog: boolean;
  showStateTransitionDialog: boolean;
  showCommentEditor: boolean;
}

export interface HierarchyState {
  expandedNodeIds: string[];
  selectedNodeId?: string;
  showCreateDialog: boolean;
  showEditDialog: boolean;
  showDeleteDialog: boolean;
}

// ================================
// Form Types
// ================================

export interface CaseFormData {
  title: string;
  description: string;
  priority: CasePriority;
  dueDate: string;
  complianceNodeId: string;
}

export interface CommentFormData {
  content: string;
}

export interface AttachmentUpload {
  file: File;
  tags: string[];
  progress: number;
  status: "pending" | "uploading" | "complete" | "error";
  error?: string;
}

// ================================
// API Response Types
// ================================

export interface ApiResponse<T> {
  data: T;
  success: boolean;
  message?: string;
  errors?: string[];
}

export interface PaginatedResponse<T> {
  items: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface ApiError {
  message: string;
  code?: string;
  details?: Record<string, any>;
  statusCode: number;
}

// ================================
// Configuration Types
// ================================

export interface AttachmentTagConfiguration {
  id: string;
  tagName: string;
  description?: string;
  color?: string;
  isRequired: boolean;
  isActive: boolean;
  companyId: string;
  createdAt: string;
  updatedAt: string;
}

export interface SystemConfiguration {
  maxAttachmentSize: number;
  allowedFileTypes: string[];
  autoAssignmentEnabled: boolean;
  notificationSettings: {
    emailEnabled: boolean;
    smsEnabled: boolean;
    inAppEnabled: boolean;
  };
  workflowSettings: {
    requireAttachmentForReview: boolean;
    requireCommentForStateChange: boolean;
    autoEscalationDays: number;
  };
}

// ================================
// Utility Types
// ================================

export type Optional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;
export type WithTimestamps<T> = T & {
  createdAt: string;
  updatedAt: string;
};

// ================================
// Advanced Search Types
// ================================

export interface SearchFilters {
  state?: CaseState;
  priority?: CasePriority;
  assignee?: string;
  assignedTo?: string;
  createdDateFrom?: string;
  createdDateTo?: string;
  tags?: string[];
  dateRange?: string;
}

export interface SearchQuery {
  query: string;
  filters: SearchFilters;
  timestamp?: number;
}

export interface SavedSearch {
  id: string;
  name: string;
  query: string;
  filters: SearchFilters;
}

export interface SearchResults {
  total: number;
  query: string;
  appliedFilters: SearchFilters;
  filters?: SearchFilters;
  page?: number;
  pageSize?: number;
}

export interface QuickFilter {
  label: string;
  filters: SearchFilters;
}

export type CaseWithRelations = Case & {
  complianceNode: ComplianceNode;
  assignedUsers: UserInfo[];
  createdByUser: UserInfo;
};

export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

// ================================
// Activity Log Types (Backend API)
// ================================

/**
 * User basic info from case history API
 */
export interface CaseHistoryPerformedBy {
  id: number;
  name: string;
  username: string;
}

/**
 * Case history entry from backend API (GET /cases/{caseId}/history)
 */
export interface CaseHistoryDto {
  id: number;
  caseId: number;
  action: string;
  fieldName: string | null;
  oldValue: string | null;
  newValue: string | null;
  metadata: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  performedBy: CaseHistoryPerformedBy | null;
  performedAt: string;
}

/**
 * Paginated response for case history
 */
export interface CaseHistoryPageResponse {
  data: CaseHistoryDto[];
  page: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
}

/**
 * Change type for audit log entries
 */
export type AuditLogChangeType = "ADDED" | "CHANGED" | "REMOVED";

/**
 * Field-level change details
 */
export interface AuditLogChange {
  field: string;
  fieldLabel: string;
  oldValue: string | null;
  newValue: string | null;
  oldDisplayValue: string | null;
  newDisplayValue: string | null;
  changeType: AuditLogChangeType;
}

/**
 * Activity log entry (audit log) from backend API
 */
export interface AuditLogEntry {
  id: number;
  action: string;
  actionDescription: string;
  entityName: string;
  performedByName: string;
  performedById: number;
  performedAt: string;
  changes: AuditLogChange[];
  summary: string;
  entityClass: string;
  entityId: string;
  tenantId: number;
  tenantName: string;
}

// ================================
// Re-Evaluation Types
// ================================

/**
 * Response from case re-evaluation endpoint
 */
export interface ReEvaluateResponse {
  success: boolean;
  caseId: number;
  documentId: number;
  jobId: number;
  message: string;
  s3Key: string;
  s3Bucket: string;
  mergedFileCount: number;
}

/**
 * Error response from re-evaluation endpoint
 */
export interface ReEvaluateErrorResponse {
  success: false;
  error: string;
}

// ================================
// Linked Documents Types
// ================================

/**
 * Evaluation status for linked documents
 * 1 = compliant, 2 = non_compliant, 3 = insufficient_evidence
 */
export const LinkedDocumentEvaluationStatus = {
  Compliant: 1,
  NonCompliant: 2,
  InsufficientEvidence: 3,
} as const;

export type LinkedDocumentEvaluationStatus =
  (typeof LinkedDocumentEvaluationStatus)[keyof typeof LinkedDocumentEvaluationStatus];

/**
 * Labels for evaluation status display
 */
export const LinkedDocumentEvaluationStatusLabels: Record<number, string> = {
  [LinkedDocumentEvaluationStatus.Compliant]: "Compliant",
  [LinkedDocumentEvaluationStatus.NonCompliant]: "Non-Compliant",
  [LinkedDocumentEvaluationStatus.InsufficientEvidence]:
    "Insufficient Evidence",
};

/**
 * Colors for evaluation status badges
 */
export const LinkedDocumentEvaluationStatusColors: Record<
  number,
  "success" | "error" | "warning"
> = {
  [LinkedDocumentEvaluationStatus.Compliant]: "success",
  [LinkedDocumentEvaluationStatus.NonCompliant]: "error",
  [LinkedDocumentEvaluationStatus.InsufficientEvidence]: "warning",
};

/**
 * Linked document from document_rules table
 * Documents that have been evaluated against the case's triggered rule
 */
export interface LinkedDocument {
  documentId: number;
  documentFilename: string;
  documentOriginalFilename: string;
  documentS3Key: string;
  documentFileSize: number;
  ruleId: number | null;
  evaluationStatus: number | null;
  evaluatedAt: string | null;
  evaluatedByUserId: number | null;
  jobId: number | null;
}

/**
 * Document explicitly linked to a case (from case_documents table).
 * Same DTO as LinkedDocument but rule/evaluation fields are always null.
 */
export type CaseDocument = Omit<
  LinkedDocument,
  "ruleId" | "evaluationStatus" | "evaluatedAt" | "evaluatedByUserId" | "jobId"
> & {
  ruleId: null;
  evaluationStatus: null;
  evaluatedAt: null;
  evaluatedByUserId: null;
  jobId: null;
};

/**
 * Paginated response for case documents
 */
export interface CaseDocumentsResponse {
  data: CaseDocument[];
  page: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
}

/**
 * Paginated response for linked documents
 */
export interface LinkedDocumentsResponse {
  success: boolean;
  data: {
    data: LinkedDocument[];
    page: number;
    pageSize: number;
    totalElements: number;
    totalPages: number;
  };
  error: null | { code: string; message: string };
  timestamp: number;
}

/**
 * Case Creation Type Constants (ALH-177)
 * Identifies whether a case was created manually or automatically by the system
 */
export const CaseCreationType = {
  /** System-generated case from automated compliance checks */
  Auto: "AUTO",
  /** Manually created case by a user */
  Manual: "MANUAL",
  /** Case created from compliance confirmation */
  ComplianceConfirmed: "COMPLIANCE_CONFIRMED",
} as const;

export type CaseCreationTypeValue =
  (typeof CaseCreationType)[keyof typeof CaseCreationType];

/**
 * Labels for case creation type display
 */
export const CaseCreationTypeLabels: Record<string, string> = {
  [CaseCreationType.Auto]: "Auto",
  [CaseCreationType.Manual]: "Manual",
  [CaseCreationType.ComplianceConfirmed]: "Compliance Confirmed",
};

/**
 * Colors for case creation type badges (MUI Chip color prop values)
 */
export const CaseCreationTypeColors: Record<
  string,
  "default" | "primary" | "secondary" | "info"
> = {
  [CaseCreationType.Auto]: "info",
  [CaseCreationType.Manual]: "secondary",
  [CaseCreationType.ComplianceConfirmed]: "info",
};

/**
 * Helper function to determine if a case was manually created
 * Per ALH-177: creationType is null for manually created cases until BE fix
 * @param creationType - The creationType value from the API
 * @returns true if the case was manually created
 */
export function isCaseManuallyCreated(
  creationType: string | null | undefined,
): boolean {
  // AUTO and COMPLIANCE_CONFIRMED = system created, null/undefined/MANUAL = manually created
  return (
    creationType !== CaseCreationType.Auto &&
    creationType !== CaseCreationType.ComplianceConfirmed
  );
}

/**
 * Helper function to get the display label for case creation type
 * Per ALH-177: null/undefined = Manual, AUTO = Auto
 * @param creationType - The creationType value from the API
 * @returns Display label ("Manual" or "Auto")
 */
export function getCaseCreationTypeLabel(
  creationType: string | null | undefined,
): string {
  if (creationType === CaseCreationType.Auto) {
    return CaseCreationTypeLabels[CaseCreationType.Auto];
  }
  if (creationType === CaseCreationType.ComplianceConfirmed) {
    return CaseCreationTypeLabels[CaseCreationType.ComplianceConfirmed];
  }
  return CaseCreationTypeLabels[CaseCreationType.Manual];
}

/**
 * Helper function to get the chip color for case creation type
 * @param creationType - The creationType value from the API
 * @returns MUI Chip color value
 */
export function getCaseCreationTypeColor(
  creationType: string | null | undefined,
): "default" | "primary" | "secondary" | "info" {
  if (creationType === CaseCreationType.Auto) {
    return CaseCreationTypeColors[CaseCreationType.Auto];
  }
  if (creationType === CaseCreationType.ComplianceConfirmed) {
    return CaseCreationTypeColors[CaseCreationType.ComplianceConfirmed];
  }
  return CaseCreationTypeColors[CaseCreationType.Manual];
}
